import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import FacetPanelFooter from './facet_panel_footer';
import FacetPanelHelp from './facet_panel_help';
import FacetPanelLoading from './facet_panel_loading';
import FacetPanelBody from './facet_panel_body';

const { LP } = window;

const FacetPanel = ({ autoload, panelId, context, keyForFacetState }) => {
  const [state, setState] = useState();
  const panelStore = context.getStore('PanelStore');
  const facetStore = context.getStore('FacetStore');

  const setStateFromStores = () => {
    const panel = panelStore.get(panelId);
    if (!panel) return;
    const facets = panel.facetContexts.reduce((memo, facetContext) => {
      memo[facetContext] = facetStore.get(facetContext, {
        sorted: panelId !== 'grade_ids'
      });
      return memo;
    }, {});

    setState({
      panel,
      facets
    });
  };

  const handleChange = () => {
    setStateFromStores();
  };

  useEffect(() => {
    setStateFromStores();
    panelStore.addChangeListener(handleChange);
    facetStore.addChangeListener(handleChange);

    return () => {
      panelStore.removeChangeListener(handleChange);
      facetStore.removeChangeListener(handleChange);
    };
  }, []);

  if (!state) {
    return null;
  }

  const extraClassName = () => {
    switch (panelId) {
      case 'type_ids':
        return 'resource-types';
      case 'grade_ids':
        return 'grades pt-1.5';
      case 'provider_ids':
        return 'resource-pools';
      case 'ss_ids':
        return 'state-standards';
      default:
        return '';
    }
  };

  if (autoload && state.facets[panelId]?.length === 0) {
    return (
      <FacetPanelLoading title={state.panel.name} extraClassName={extraClassName()} />
    );
  }

  const mainClasses = ['lp-filter-group border-b', `parent-${panelId}`];

  if (state.panel.collapsed) {
    mainClasses.push('closed');
  }

  const handleMouseDown = (e) => {
    if (e.target.className === 'lp-icon fa-regular fa-circle-info ml5') return;

    context.executeAction(LP.module('Searches').SearchActions.togglePanelCollapse, {
      keys: panelId,
      userTriggered: true,
      keyForFacetState
    });
  };

  return (
    <div className={mainClasses.join(' ')}>
      <div className={`lp-filter-head`}>
        <h4
          className="lp-filter-title m-0 py-3 text-sm text-gray-800 font-bold group"
          onMouseDown={handleMouseDown}
        >
          <span className="name" title={state.panel.name}>
            {state.panel.name}
          </span>
          {LP.config.isDesktop && state.panel.popoverDescription && (
            <FacetPanelHelp
              panelId={panelId}
              popoverDescription={state.panel.popoverDescription}
            />
          )}
          <i
            className={`lp-icon-arrow group-hover:inline-block fa-regular sm:block ${
              state.panel.collapsed
                ? 'block fa-angle-right'
                : 'lg:hidden fa-angle-down mr-1'
            }`}
          />
        </h4>
      </div>
      {!state.panel.collapsed && (
        <div className={`lp-filter-body pb-2 -mt-1 ${extraClassName()}`}>
          <FacetPanelBody
            context={context}
            facets={state.facets}
            panel={state.panel}
            panelId={panelId}
          />
          <FacetPanelFooter
            panelId={panelId}
            context={context}
            showMore={state.panel.showMore}
          />
        </div>
      )}
    </div>
  );
};

FacetPanel.propTypes = {
  autoload: PropTypes.bool,
  panelId: PropTypes.string,
  context: PropTypes.object,
  keyForFacetState: PropTypes.string
};

export default FacetPanel;
